import React, { useState } from "react";
import { createRoot } from "react-dom/client";
import { Button, Modal, Spinner } from "flowbite-react";
import { HiOutlineExclamationCircle } from "react-icons/hi";

const confirmDialogTheme = {
  root: {
    base: "fixed inset-0 z-[2000] h-full w-full overflow-y-auto overflow-x-hidden md:inset-0 md:h-full",
  },
  content: {
    inner:
      "relative rounded-lg bg-white shadow dark:bg-gray-700 flex flex-col max-h-full",
  },
  backdrop: "fixed inset-0 z-[1990] bg-gray-900/50 dark:bg-gray-900/80",
};

function ConfirmDialog({
  show,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  color = "blue",
  loading = false,
  loadingText = "Processing...",
  onConfirm,
  onClose,
}) {
  return (
    <Modal
      show={show}
      size="md"
      onClose={onClose}
      popup
      theme={confirmDialogTheme}
    >
      <Modal.Header />
      <Modal.Body>
        <div className="text-center">
          <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-yellow-500" />
          <h3 className="mb-3 text-lg font-semibold text-gray-900 dark:text-white">
            {title}
          </h3>
          <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">
            {message}
          </p>
          <div className="flex justify-center gap-4">
            <Button color={color} onClick={onConfirm} disabled={loading}>
              {loading ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  {loadingText}
                </>
              ) : (
                confirmText
              )}
            </Button>
            <Button color="gray" onClick={onClose} disabled={loading}>
              {cancelText}
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default ConfirmDialog;

export function confirmDialog(message, options = {}) {
  return new Promise((resolve, reject) => {
    const container = document.createElement("div");
    document.body.appendChild(container);
    const root = createRoot(container);

    const cleanup = (value) => {
      root.unmount();
      container.remove();
      resolve(value);
    };

    function ConfirmDialogContainer() {
      const [loading, setLoading] = useState(false);

      const handleConfirm = async () => {
        if (!options.onConfirm) {
          cleanup(true);
          return;
        }

        setLoading(true);
        try {
          await options.onConfirm();
          cleanup(true);
        } catch (error) {
          setLoading(false);
          reject(error);
        }
      };

      const handleClose = () => {
        if (!loading) cleanup(false);
      };

      return (
        <ConfirmDialog
          show
          message={message}
          title={options.title || "Please Confirm"}
          confirmText={options.confirmText || "Confirm"}
          cancelText={options.cancelText || "Cancel"}
          color={options.color || "blue"}
          loading={loading}
          loadingText={options.loadingText || "Processing..."}
          onConfirm={handleConfirm}
          onClose={handleClose}
        />
      );
    }

    root.render(<ConfirmDialogContainer />);
  });
}
